import { Query, QueryResult } from 'material-table';
import {
    collection,
    where,
    query as fsQuery,
    getCountFromServer,
    WhereFilterOp,
    limit,
    getDocs,
    orderBy,
    startAfter
} from 'firebase/firestore';
import { firestoreInstance } from 'config/ApiService';
import Brightcache from './BrightCache';

type RemoteQueryParam = {
    remoteCollectionName: string;
    fields: Array<{
        name: string;
        op: string;
        value: unknown;
    }>;
    renderData?: (data: any[]) => any[];
    customSort?: (data: any[]) => any[];
};

const paginationCache = new Brightcache<{ next: any }>();
const columnCache = new Brightcache<{ field: string }>();

/**
 * This function queries the firestore database for data
 * @remarks - This function uses a cache to store the reference to the last page visited
 * @param query
 * @param param
 * @returns
 */
export const dataQuery = async (
    query: Query<any>,
    param: RemoteQueryParam
): Promise<QueryResult<any>> => {
    try {
        const { search, pageSize, page, orderDirection } = query;
        // Hack to remember the last column filter used
        // This is done because for some reason material-table returns undefined column field
        if (query.orderBy && query.orderBy.field) {
            columnCache.set('column', {
                field: query.orderBy.field as string
            });
        }
        // This is done because for some reason material-table returns undefined column field
        // on subsequent triggers
        const orderByFieldInQuery =
            (query.orderBy?.field as string) ?? columnCache.get('column')?.field;
        const filterOrderDirection =
            query.orderBy === undefined ? 'desc' : orderDirection ?? 'desc';

        const orderByComposite = orderByFieldInQuery
            ? [orderBy(orderByFieldInQuery, filterOrderDirection)]
            : [];

        const qContraints = param.fields
            .filter((field) => field.op !== 'orderBy')
            .map((field) => where(field.name, field.op as WhereFilterOp, field.value));

        const qOrderbyConstraints = param.fields
            .filter((field) => field.op === 'orderBy')
            .map((field) => orderBy(field.name, field.value as 'asc' | 'desc'));

        if (search) {
            const searchTerm = search.trim().toLowerCase();
            if (searchTerm.length >= 3) {
                qContraints.push(where('search_index', 'array-contains', searchTerm));
            }
        }

        const base = collection(firestoreInstance(), param.remoteCollectionName);
        // Get the total count of the data with the filter constraints applied;
        const aggregateCountRef = fsQuery(base, ...qContraints);
        const { count } = (await getCountFromServer(aggregateCountRef)).data();

        // Check if the page has been visited before
        const beenThere = paginationCache.get(`${page}`);
        const pageBefore = paginationCache.get((page - 1).toString())?.next;
        if (beenThere?.next && page !== 0) {
            // If the page has been visited before, we can use the reference to get the data
            const cachedQuery = fsQuery(
                base,
                ...qContraints,
                ...qOrderbyConstraints,
                ...orderByComposite,
                startAfter(pageBefore),
                limit(pageSize)
            );
            const filterRef = await getDocs(cachedQuery);
            const result = filterRef.docs.map((docData) => ({
                ...docData.data(),
                id: docData.id
            }));
            return {
                data: param.renderData
                    ? param.renderData(result)
                    : param.customSort
                    ? param.customSort(result)
                    : result,
                page,
                totalCount: count
            };
        }
        if (page === 0 && !pageBefore) {
            // If the page is the first page or there is no page before it
            const qRef = fsQuery(
                base,
                ...qContraints,
                ...qOrderbyConstraints,
                ...orderByComposite,
                limit(pageSize)
            );
            const filterRef = await getDocs(qRef);
            const result = filterRef.docs.map((docData) => ({
                ...docData.data(),
                id: docData.id
            }));
            paginationCache.set(`${page}`, {
                next: filterRef.docs[filterRef.docs.length - 1]
            });
            return {
                data: param.renderData
                    ? param.renderData(result)
                    : param.customSort
                    ? param.customSort(result)
                    : result,
                page,
                totalCount: count
            };
        }
        // If the page is not the first page and there is a page before it
        const paginatedRef = fsQuery(
            base,
            ...qContraints,
            ...orderByComposite,
            startAfter(pageBefore),
            limit(pageSize)
        );
        const bRef = await getDocs(paginatedRef);
        const paginatedResult = bRef.docs.map((docData) => ({
            ...docData.data(),
            id: docData.id
        }));
        paginationCache.set(page.toString(), {
            next: bRef.docs[bRef.docs.length - 1]
        });
        return {
            data: param.renderData
                ? param.renderData(paginatedResult)
                : param.customSort
                ? param.customSort(paginatedResult)
                : paginatedResult,
            page,
            totalCount: count
        };
    } catch (e) {
        console.error(e);
        return {
            data: [],
            page: 0,
            totalCount: 0
        };
    }
};
